import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
  ViewEncapsulation
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { EsriViewType, JepBasemap } from '@jeppesen-foreflight/jmkw-types';
import { map } from 'rxjs/operators';
import { BasemapManagerService } from '../../lib/arcgis/basemaps/basemap-manager.service';
import { ViewService } from '../../lib/arcgis/map-view/views/views.service';

@Component({
  selector: 'jmkw-basemap-selector',
  standalone: true,
  templateUrl: './basemap-selector.component.html',
  styleUrls: ['./basemap-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // ViewEncapsulation.None so the global theming rules in
  // ThemeService.buildCalciteOverrideCss() can reach .basemap-toggle,
  // .basemap-list, and .basemap-item by their class names.
  encapsulation: ViewEncapsulation.None
})
export class BasemapSelectorComponent implements OnDestroy {
  private readonly _basemapManager = inject(BasemapManagerService);
  private readonly _viewService = inject(ViewService);
  private readonly _elementRef = inject(ElementRef);

  // @arcgis/core v5 re-parents the DOM node passed to view.ui.add() into the
  // view's own UI container. A single component instance cannot be shared
  // across MapView and SceneView — the second add() steals the node from the
  // first. Each view gets its own instance, bound via this input.
  readonly viewType = input.required<EsriViewType>();

  readonly expanded = signal(false);

  readonly basemaps = toSignal(
    this._basemapManager.basemaps$.pipe(
      map((available) =>
        Object.values(available).filter((b): b is JepBasemap => !!b)
      )
    ),
    { initialValue: [] as JepBasemap[] }
  );

  readonly currentBasemap = toSignal(this._basemapManager.currentBasemap$);

  readonly showSelector = computed(() => this.basemaps().length > 1);

  private readonly view = computed(() =>
    this.viewType() === '2D'
      ? this._viewService.$mapView()
      : this._viewService.$sceneView()
  );

  constructor() {
    effect(() => {
      const view = this.view();
      const show = this.showSelector();
      if (!view?.ui) return;
      if (show) {
        view.ui.add(this._elementRef.nativeElement, { position: 'top-left', index: 0 });
      } else {
        view.ui.remove(this._elementRef.nativeElement);
      }
    });
  }

  ngOnDestroy(): void {
    this.view()?.ui?.remove(this._elementRef.nativeElement);
  }

  toggle(): void {
    this.expanded.update((v) => !v);
  }

  selectBasemap(basemap: JepBasemap): void {
    this._basemapManager.setBasemapById(basemap.id ?? undefined);
    this.expanded.set(false);
  }
}
